import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import type {
  CanvasMode,
  ComposerTab,
  DiagramRevision,
  DiagramSortMode,
  MobilePane,
  PendingImage,
  PipelineStage,
  Theme,
} from './store/types.ts';
import { ONBOARDING_KEY, STAGE_LABELS, STARTER_TEMPLATES } from './store/types.ts';
import { persistStore, readBootState, safeWrite } from './store/persistence.ts';
import {
  appendChat,
  commitDiagramCode,
  createDiagramUpdater,
  createProjectUpdater,
  deleteDiagramUpdater,
  deleteProjectUpdater,
  patchDiagram,
  redoDiagramUpdater,
  renameProjectUpdater,
  restoreRevisionUpdater,
  selectProjectUpdater,
  undoDiagramUpdater,
  togglePinDiagramUpdater,
  addTagUpdater,
  removeTagUpdater,
  updateDiagramSortModeUpdater,
  updateModelUpdater,
} from './store/studio-store.ts';
import { readImageFile } from './lib/claude-prompts.ts';
import { exportMermaidSource, exportPng, exportSvg } from './lib/export-utils.ts';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts.ts';
import { useMermaidRenderer } from './hooks/useMermaidRenderer.ts';
import { useGeneration } from './hooks/useGeneration.ts';
import { useServerSync } from './hooks/useServerSync.ts';
import { useCanvasInteraction } from './hooks/useCanvasInteraction.ts';
import { useExcalidraw } from './hooks/useExcalidraw.ts';
import { useAuth } from './auth/AuthContext.tsx';
import { Header } from './components/layout/Header.tsx';
import { StatusBar } from './components/layout/StatusBar.tsx';
import { Sidebar } from './components/sidebar/Sidebar.tsx';
import { PromptPanel } from './components/prompt/PromptPanel.tsx';
import { CanvasPanel } from './components/canvas/CanvasPanel.tsx';
import { ExcalidrawEditor } from './components/canvas/ExcalidrawEditor.tsx';
import { SettingsModal } from './components/modals/SettingsModal.tsx';
import { OnboardingFlow } from './components/modals/OnboardingFlow.tsx';
import { CommandPalette } from './components/modals/CommandPalette.tsx';
import { ShortcutsHelp } from './components/modals/ShortcutsHelp.tsx';
import { PropertiesPanel } from './components/canvas/PropertiesPanel.tsx';
import { HintToast } from './components/layout/HintToast.tsx';
import { ShareModal } from './components/modals/ShareModal.tsx';

import './styles/layout.css';
import './styles/components.css';

function App() {
  // --- Boot state ---
  const [boot] = useState(() => readBootState());
  const [store, setStore] = useState(() => boot.store);

  // --- Auth ---
  const { user } = useAuth();
  const workspaceId = user?.workspace?.id;

  // --- Sync local diagrams to server once authenticated ---
  useServerSync(workspaceId, store, setStore);

  // --- UI state ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(() => !boot.onboardingDone);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [propsOpen, setPropsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ComposerTab>('chat');
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('mermaid');
  const [mobilePane, setMobilePane] = useState<MobilePane>('compose');
  const [promptText, setPromptText] = useState('');
  const [editorCode, setEditorCode] = useState('');
  const [pendingImage, setPendingImage] = useState<PendingImage | null>(null);
  const [diagramSearch, setDiagramSearch] = useState('');
  const [pipelineStage, setPipelineStage] = useState<PipelineStage>('idle');
  const [statusText, setStatusText] = useState('Ready');

  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  // --- Derived state ---
  const selectedProject = useMemo(
    () => store.projects.find((p) => p.id === store.selectedProjectId),
    [store.projects, store.selectedProjectId],
  );

  const selectedDiagram = useMemo(
    () => store.diagrams.find((d) => d.id === store.selectedDiagramId),
    [store.diagrams, store.selectedDiagramId],
  );

  const filteredDiagrams = useMemo(() => {
    const query = diagramSearch.trim().toLowerCase();
    const list = store.diagrams
      .filter((d) => d.projectId === store.selectedProjectId)
      .filter((d) => {
        if (!query) return true;
        return d.title.toLowerCase().includes(query) || d.mermaidCode.toLowerCase().includes(query) || (d.tags || []).some((t) => t.includes(query));
      });
    list.sort((a, b) => {
      // Pinned diagrams always come first
      const pa = a.pinned ? 1 : 0;
      const pb = b.pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;
      if (store.diagramSortMode === 'alpha') return a.title.localeCompare(b.title);
      if (store.diagramSortMode === 'oldest') return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return list;
  }, [store.diagrams, store.selectedProjectId, diagramSearch, store.diagramSortMode]);

  const revisionList = useMemo(() => {
    if (!selectedDiagram) return [] as Array<DiagramRevision & { index: number }>;
    return selectedDiagram.revisions.map((r, i) => ({ ...r, index: i })).reverse();
  }, [selectedDiagram]);

  const canUndo = Boolean(selectedDiagram && selectedDiagram.revisionCursor > 0);
  const canRedo = Boolean(selectedDiagram && selectedDiagram.revisionCursor < selectedDiagram.revisions.length - 1);

  // --- Persistence effects ---
  useEffect(() => { persistStore(store); }, [store]);

  // Reset prompt when diagram changes
  const prevDiagramIdRef = useRef(selectedDiagram?.id);
  if (prevDiagramIdRef.current !== selectedDiagram?.id) {
    prevDiagramIdRef.current = selectedDiagram?.id;
    if (promptText) setPromptText('');
    if (pendingImage) setPendingImage(null);
  }

  // Sync editor code with diagram
  const derivedEditorCode = selectedDiagram?.mermaidCode || '';
  const prevMermaidCodeRef = useRef(derivedEditorCode);
  if (prevMermaidCodeRef.current !== derivedEditorCode) {
    prevMermaidCodeRef.current = derivedEditorCode;
    setEditorCode(derivedEditorCode);
  }

  // Pipeline completion auto-reset
  useEffect(() => {
    if (pipelineStage !== 'completed') return;
    const timer = window.setTimeout(() => setPipelineStage('idle'), 1400);
    return () => window.clearTimeout(timer);
  }, [pipelineStage]);

  // --- Canvas interaction ---
  const canvas = useCanvasInteraction();

  // --- Excalidraw ---
  const excalidraw = useExcalidraw(selectedDiagram?.id);

  // --- Mermaid renderer ---
  const onPipelineChange = useCallback((stage: PipelineStage) => setPipelineStage(stage), []);
  const onStatusChange = useCallback((text: string) => setStatusText(text), []);

  const { renderedSvg, renderError, isRendering } = useMermaidRenderer(
    selectedDiagram?.mermaidCode,
    selectedDiagram?.theme || 'light',
    pipelineStage,
    onPipelineChange,
    onStatusChange,
  );

  // --- Auto-fit diagram after render completes ---
  const prevSvgRef = useRef('');
  useEffect(() => {
    if (renderedSvg && renderedSvg !== prevSvgRef.current) {
      prevSvgRef.current = renderedSvg;
      // Small delay so the DOM has the SVG before fitToView measures it
      requestAnimationFrame(() => canvas.fitToView());
    }
  }, [renderedSvg, canvas.fitToView]);

  // --- Generation API ---
  const generationHandlers = useMemo(
    () => ({
      onCommitCode: (diagramId: string, code: string, source: 'chat' | 'autofix', note: string) => {
        setStore(commitDiagramCode(diagramId, code, source, note));
        setEditorCode(code);
        setActiveTab('code');
        setMobilePane('canvas');
      },
      onAppendChat: (diagramId: string, role: 'user' | 'assistant' | 'error', text: string) => {
        setStore(appendChat(diagramId, role, text));
      },
      onExcalidrawGenerated: (data: import('./store/types.ts').ExcalidrawData) => {
        excalidraw.setExcalidrawFromAI(data);
        setCanvasMode('draw');
        setMobilePane('canvas');
      },
      onPipelineChange,
      onStatusChange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onPipelineChange, onStatusChange, excalidraw.setExcalidrawFromAI],
  );

  const generation = useGeneration(workspaceId, canvasMode, selectedDiagram, generationHandlers);

  // --- Keyboard shortcuts ---
  useKeyboardShortcuts({
    onFocusPrompt: () => {
      setMobilePane('compose');
      setActiveTab('chat');
      promptRef.current?.focus();
    },
    onSend: () => {
      const elems = canvasMode === 'draw' ? (excalidraw.excalidrawData?.elements || []) : undefined;
      void generation.sendPrompt(promptText, pendingImage, elems);
    },
    onSave: () => {
      if (selectedDiagram) {
        setStore(commitDiagramCode(selectedDiagram.id, editorCode, 'editor', 'Rendered from editor'));
        setStatusText('Editor changes rendered');
        setPipelineStage('idle');
      }
    },
    onUndo: () => {
      if (selectedDiagram && canUndo) {
        setStore(undoDiagramUpdater(selectedDiagram.id));
        setStatusText('Undid latest change');
        setActiveTab('code');
      }
    },
    onRedo: () => {
      if (selectedDiagram && canRedo) {
        setStore(redoDiagramUpdater(selectedDiagram.id));
        setStatusText('Redid change');
        setActiveTab('code');
      }
    },
    onToggleFullscreen: () => { void canvas.toggleFullscreen(); },
    onToggleSidebar: () => setSidebarOpen((v) => !v),
    onOpenCommandPalette: () => setCommandPaletteOpen((v) => !v),
    onOpenShortcutsHelp: () => setShortcutsHelpOpen(true),
    onSwitchTab: (tab: string) => setActiveTab(tab as ComposerTab),
    onExport: () => {
      if (renderedSvg && selectedDiagram) exportSvg(renderedSvg, selectedDiagram.title, selectedDiagram.theme);
    },
    onToggleProperties: () => setPropsOpen((v) => !v),
    isSending: generation.isSending,
    activeTab,
  });

  // --- Action handlers ---
  const handleSend = useCallback(async () => {
    const currentElements = canvasMode === 'draw' ? (excalidraw.excalidrawData?.elements || []) : undefined;
    const result = await generation.sendPrompt(promptText, pendingImage, currentElements);
    if (result !== undefined || canvasMode === 'draw') {
      setPromptText('');
      setPendingImage(null);
    }
  }, [generation, promptText, pendingImage, canvasMode, excalidraw.excalidrawData]);

  const handleAttachImage = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const image = await readImageFile(file);
    setPendingImage(image);
    event.target.value = '';
  }, []);

  const handleFinishOnboarding = useCallback(
    (templateId: string) => {
      safeWrite(localStorage, ONBOARDING_KEY, '1');
      setOnboardingOpen(false);
      const selected = STARTER_TEMPLATES.find((t) => t.id === templateId);
      if (selected) {
        setPromptText(selected.prompt);
        setActiveTab('chat');
        setMobilePane('compose');
        promptRef.current?.focus();
      }
    },
    [],
  );

  const handleDiagramTypePrefix = useCallback((prefix: string) => {
    setPromptText(prefix);
    setActiveTab('chat');
    setMobilePane('compose');
    promptRef.current?.focus();
  }, []);

  // --- Command palette actions ---
  const commandActions = useMemo(() => [
    { id: 'focus-prompt', label: 'Focus prompt', shortcut: '/', section: 'Navigation', onAction: () => { setActiveTab('chat'); promptRef.current?.focus(); } },
    { id: 'toggle-sidebar', label: 'Toggle sidebar', shortcut: '⌘B', section: 'Navigation', onAction: () => setSidebarOpen((v) => !v) },
    { id: 'fullscreen', label: 'Toggle fullscreen', shortcut: 'F', section: 'Navigation', onAction: () => { void canvas.toggleFullscreen(); } },
    { id: 'settings', label: 'Open settings', section: 'Navigation', onAction: () => setSettingsOpen(true) },
    { id: 'shortcuts', label: 'Keyboard shortcuts', shortcut: '?', section: 'Navigation', onAction: () => setShortcutsHelpOpen(true) },
    { id: 'properties', label: 'Toggle properties panel', shortcut: 'I', section: 'Navigation', onAction: () => setPropsOpen((v) => !v) },
    { id: 'tab-chat', label: 'Switch to Chat tab', section: 'Tabs', onAction: () => setActiveTab('chat') },
    { id: 'tab-code', label: 'Switch to Code tab', section: 'Tabs', onAction: () => setActiveTab('code') },
    { id: 'tab-history', label: 'Switch to History tab', section: 'Tabs', onAction: () => setActiveTab('history') },
    { id: 'mode-mermaid', label: 'Mermaid canvas mode', section: 'Canvas', onAction: () => setCanvasMode('mermaid') },
    { id: 'mode-draw', label: 'Excalidraw canvas mode', section: 'Canvas', onAction: () => setCanvasMode('draw') },
    { id: 'export-svg', label: 'Export as SVG', section: 'Export', onAction: () => { if (renderedSvg && selectedDiagram) exportSvg(renderedSvg, selectedDiagram.title, selectedDiagram.theme); } },
    { id: 'export-png', label: 'Export as PNG (2x)', section: 'Export', onAction: () => { if (renderedSvg && selectedDiagram) void exportPng(renderedSvg, selectedDiagram.title, selectedDiagram.theme, 2); } },
    { id: 'export-mmd', label: 'Export as .mmd file', section: 'Export', onAction: () => { if (selectedDiagram?.mermaidCode.trim()) exportMermaidSource(selectedDiagram.mermaidCode, selectedDiagram.title); } },
    { id: 'undo', label: 'Undo', shortcut: '⌘Z', section: 'Edit', onAction: () => { if (selectedDiagram && canUndo) { setStore(undoDiagramUpdater(selectedDiagram.id)); setStatusText('Undid latest change'); } } },
    { id: 'redo', label: 'Redo', shortcut: '⌘⇧Z', section: 'Edit', onAction: () => { if (selectedDiagram && canRedo) { setStore(redoDiagramUpdater(selectedDiagram.id)); setStatusText('Redid change'); } } },
    { id: 'theme-light', label: 'Set light theme', section: 'Canvas', onAction: () => { if (selectedDiagram) setStore(patchDiagram(selectedDiagram.id, { theme: 'light' })); } },
    { id: 'theme-dark', label: 'Set dark theme', section: 'Canvas', onAction: () => { if (selectedDiagram) setStore(patchDiagram(selectedDiagram.id, { theme: 'dark' })); } },
    { id: 'new-diagram', label: 'New diagram', section: 'Project', onAction: () => { if (selectedProject) { const d = `Diagram ${store.diagrams.filter(x => x.projectId === selectedProject.id).length + 1}`; setStore(createDiagramUpdater(selectedProject.id, d)); setActiveTab('chat'); } } },
    { id: 'new-project', label: 'New project', section: 'Project', onAction: () => { setStore(createProjectUpdater('New Project')); } },
  ], [canvas, renderedSvg, selectedDiagram, selectedProject, canUndo, canRedo, store.diagrams, setStore]);

  return (
    <div className="ds-app" data-mobile-pane={mobilePane}>
      <div className="sr-only" role="status" aria-live="polite">
        {`${STAGE_LABELS[pipelineStage]}. ${statusText}`}
      </div>

      <Header
        project={selectedProject}
        diagram={selectedDiagram}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        onOpenSettings={() => setSettingsOpen(true)}
        onRenameDiagram={() => {
          if (!selectedDiagram) return;
          const title = window.prompt('Rename diagram', selectedDiagram.title)?.trim();
          if (title) setStore(patchDiagram(selectedDiagram.id, { title }));
        }}
        onOpenSidebar={() => setSidebarOpen(true)}
        onShare={() => setShareModalOpen(true)}
      />

      <div className="ds-body">
        {sidebarOpen && (
          <div className="ds-sidebar-overlay ds-sidebar-overlay--visible" onClick={() => setSidebarOpen(false)} />
        )}

        <Sidebar
          open={sidebarOpen}
          projects={store.projects}
          diagrams={filteredDiagrams}
          selectedProjectId={store.selectedProjectId}
          selectedDiagramId={store.selectedDiagramId}
          diagramSearch={diagramSearch}
          diagramSortMode={store.diagramSortMode}
          onClose={() => setSidebarOpen(false)}
          onSelectProject={(id) => {
            setStore(selectProjectUpdater(id));
            setMobilePane('compose');
          }}
          onCreateProject={() => {
            const name = window.prompt('Project name', 'New Project')?.trim();
            if (name) {
              setStore(createProjectUpdater(name));
              setMobilePane('compose');
            }
          }}
          onRenameProject={(id) => {
            const project = store.projects.find((p) => p.id === id);
            if (!project) return;
            const name = window.prompt('Rename project', project.name)?.trim();
            if (name) setStore(renameProjectUpdater(id, name));
          }}
          onDeleteProject={(id) => {
            if (window.confirm('Delete this project and all its diagrams?')) {
              setStore(deleteProjectUpdater(id));
            }
          }}
          onSelectDiagram={(id) => {
            setStore((prev) => ({ ...prev, selectedDiagramId: id }));
            setMobilePane('compose');
          }}
          onCreateDiagram={() => {
            if (!selectedProject) return;
            const title = window.prompt('Diagram title', 'Untitled Diagram')?.trim();
            if (title) {
              setStore(createDiagramUpdater(selectedProject.id, title));
              setActiveTab('chat');
              setMobilePane('compose');
            }
          }}
          onRenameDiagram={(id) => {
            const diagram = store.diagrams.find((d) => d.id === id);
            if (!diagram) return;
            const title = window.prompt('Rename diagram', diagram.title)?.trim();
            if (title) setStore(patchDiagram(id, { title }));
          }}
          onDeleteDiagram={(id) => {
            if (window.confirm('Delete this diagram?')) {
              setStore(deleteDiagramUpdater(id));
            }
          }}
          onPinDiagram={(id) => setStore(togglePinDiagramUpdater(id))}
          onSearchChange={setDiagramSearch}
          onSortChange={(mode: DiagramSortMode) => setStore(updateDiagramSortModeUpdater(mode))}
        />

        <div className="ds-workspace">
          <PromptPanel
            activeTab={activeTab}
            onTabChange={setActiveTab}
            chat={selectedDiagram?.chat || []}
            isSending={generation.isSending}
            pipelineStage={pipelineStage}
            pendingImage={pendingImage}
            promptText={promptText}
            hasLastRequest={Boolean(generation.lastRequest)}
            onPromptChange={setPromptText}
            onSend={() => void handleSend()}
            onRetry={() => void generation.retryLast()}
            onClearChat={() => {
              if (selectedDiagram) setStore(patchDiagram(selectedDiagram.id, { chat: [] }));
            }}
            onStop={generation.stopRequest}
            onAutoFix={() => void generation.autoFix()}
            onAttachImage={(e) => void handleAttachImage(e)}
            onRemoveImage={() => setPendingImage(null)}
            promptRef={promptRef}
            editorCode={editorCode}
            onEditorChange={setEditorCode}
            onRender={() => {
              if (selectedDiagram) {
                setStore(commitDiagramCode(selectedDiagram.id, editorCode, 'editor', 'Rendered from editor'));
                setStatusText('Editor changes rendered');
                setPipelineStage('idle');
              }
            }}
            onCopy={async () => {
              if (selectedDiagram?.mermaidCode.trim()) {
                try {
                  await navigator.clipboard.writeText(selectedDiagram.mermaidCode);
                  setStatusText('Copied Mermaid to clipboard');
                } catch {
                  setStatusText('Clipboard copy failed');
                }
              }
            }}
            onExportMmd={() => {
              if (selectedDiagram?.mermaidCode.trim()) {
                exportMermaidSource(selectedDiagram.mermaidCode, selectedDiagram.title);
              }
            }}
            editorRef={editorRef}
            revisions={revisionList}
            currentCursor={selectedDiagram?.revisionCursor ?? 0}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={() => {
              if (selectedDiagram && canUndo) {
                setStore(undoDiagramUpdater(selectedDiagram.id));
                setStatusText('Undid latest change');
              }
            }}
            onRedo={() => {
              if (selectedDiagram && canRedo) {
                setStore(redoDiagramUpdater(selectedDiagram.id));
                setStatusText('Redid change');
              }
            }}
            onRestore={(index) => {
              if (!selectedDiagram) return;
              const revision = selectedDiagram.revisions[index];
              if (!revision) return;
              setStore(restoreRevisionUpdater(selectedDiagram.id, index, revision));
              setStore(appendChat(selectedDiagram.id, 'assistant', `Restored revision from ${new Date(revision.createdAt).toLocaleString()}.`));
              setStatusText('Revision restored');
            }}
            onDiagramTypePrefix={handleDiagramTypePrefix}
          />

          <CanvasPanel
            diagram={selectedDiagram}
            canvasMode={canvasMode}
            onCanvasModeChange={setCanvasMode}
            renderedSvg={renderedSvg}
            renderError={renderError}
            isRendering={isRendering}
            zoom={canvas.zoom}
            pan={canvas.pan}
            isFullscreen={canvas.isFullscreen}
            spaceHeld={canvas.spaceHeld}
            isSending={generation.isSending}
            viewportRef={canvas.viewportRef}
            stageRef={canvas.stageRef}
            canvasShellRef={canvas.canvasShellRef}
            onWheel={canvas.handleWheel}
            onDragStart={canvas.handleDragStart}
            onDragMove={canvas.handleDragMove}
            onZoomIn={canvas.zoomIn}
            onZoomOut={canvas.zoomOut}
            onResetView={canvas.resetView}
            onFitToView={canvas.fitToView}
            onToggleFullscreen={() => void canvas.toggleFullscreen()}
            onSetTheme={(theme: Theme) => {
              if (selectedDiagram) setStore(patchDiagram(selectedDiagram.id, { theme }));
            }}
            onExportSvg={() => {
              if (renderedSvg && selectedDiagram) exportSvg(renderedSvg, selectedDiagram.title, selectedDiagram.theme);
            }}
            onExportPng1x={() => {
              if (renderedSvg && selectedDiagram) void exportPng(renderedSvg, selectedDiagram.title, selectedDiagram.theme, 1);
            }}
            onExportPng2x={() => {
              if (renderedSvg && selectedDiagram) void exportPng(renderedSvg, selectedDiagram.title, selectedDiagram.theme, 2);
            }}
            onAutoFix={() => void generation.autoFix()}
            onSwitchToCode={() => setActiveTab('code')}
            onToggleProperties={() => setPropsOpen((v) => !v)}
            excalidrawSlot={canvasMode === 'draw' ? (
              <ExcalidrawEditor
                key={excalidraw.generationKey}
                theme={selectedDiagram?.theme || 'dark'}
                initialData={excalidraw.excalidrawData}
                onChange={excalidraw.handleExcalidrawChange}
              />
            ) : undefined}
          />

          <PropertiesPanel
            open={propsOpen}
            onClose={() => setPropsOpen(false)}
            diagram={selectedDiagram}
            renderError={renderError}
            zoom={canvas.zoom}
            onSetTheme={(theme: Theme) => {
              if (selectedDiagram) setStore(patchDiagram(selectedDiagram.id, { theme }));
            }}
            onAddTag={(tag) => {
              if (selectedDiagram) setStore(addTagUpdater(selectedDiagram.id, tag));
            }}
            onRemoveTag={(tag) => {
              if (selectedDiagram) setStore(removeTagUpdater(selectedDiagram.id, tag));
            }}
          />
        </div>
      </div>

      <StatusBar
        statusText={statusText}
        pipelineStage={pipelineStage}
        zoom={canvas.zoom}
        onOpenShortcuts={() => setShortcutsHelpOpen(true)}
      />

      {/* Mobile nav */}
      <nav className="ds-mobile-nav" aria-label="Mobile sections">
        {(['projects', 'compose', 'canvas'] as const).map((pane) => (
          <button
            key={pane}
            type="button"
            className={`ds-mobile-nav__btn ${mobilePane === pane ? 'ds-mobile-nav__btn--active' : ''}`}
            onClick={() => {
              setMobilePane(pane);
              if (pane === 'projects') setSidebarOpen(true);
              else setSidebarOpen(false);
            }}
          >
            {pane === 'projects' ? 'Projects' : pane === 'compose' ? 'Prompt' : 'Canvas'}
          </button>
        ))}
      </nav>

      <SettingsModal
        open={settingsOpen}
        model={store.model}
        onClose={() => setSettingsOpen(false)}
        onUpdateModel={(model) => setStore(updateModelUpdater(model))}
        onRerunOnboarding={() => setOnboardingOpen(true)}
      />

      <OnboardingFlow
        open={onboardingOpen}
        onFinish={handleFinishOnboarding}
        onSkip={() => handleFinishOnboarding(STARTER_TEMPLATES[0].id)}
      />

      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        actions={commandActions}
      />

      <ShortcutsHelp
        open={shortcutsHelpOpen}
        onClose={() => setShortcutsHelpOpen(false)}
      />

      <ShareModal
        open={shareModalOpen}
        diagram={selectedDiagram}
        onClose={() => setShareModalOpen(false)}
      />

      <HintToast />
    </div>
  );
}

export default App;
